import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/supabase";
import './Login.css';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState({ count: 0, lockoutEnd: 0 });
  const [lockoutTimer, setLockoutTimer] = useState(0);

  const passwordRef = useRef("");

  useEffect(() => {
    return () => {
      setPassword("");
      passwordRef.current = "";
    };
  }, []);

  useEffect(() => {
    try {
      const storedAttempts = localStorage.getItem("loginAttempts");
      if (storedAttempts) {
        const { count, lockoutEnd } = JSON.parse(storedAttempts);
        if (lockoutEnd && lockoutEnd > Date.now()) {
          setLoginAttempts({ count, lockoutEnd });
        } else {
          localStorage.removeItem("loginAttempts");
        }
      }
    } catch {
      localStorage.removeItem("loginAttempts");
    }
  }, []);

  useEffect(() => {
    let timer;
    if (loginAttempts.lockoutEnd > Date.now()) {
      const updateTimer = () => {
        const remaining = Math.max(0, Math.round((loginAttempts.lockoutEnd - Date.now()) / 1000));
        setLockoutTimer(remaining);
        if (remaining <= 0) {
          localStorage.removeItem("loginAttempts");
          setLoginAttempts({ count: 0, lockoutEnd: 0 });
          setError(null);
        }
      };
      updateTimer();
      timer = setInterval(updateTimer, 1000);
    } else {
      setLockoutTimer(0);
    }
    return () => clearInterval(timer);
  }, [loginAttempts]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (loginAttempts.lockoutEnd > Date.now()) {
      setError(`Too many failed attempts. Try again in ${lockoutTimer}s.`);
      return;
    }

    setIsLoading(true);
    setError(null);

    const passwordValue = passwordRef.current;
    setPassword("");
    passwordRef.current = "";

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password: passwordValue });

      if (signInError) {
        let currentAttempts = 0;
        try {
          const stored = JSON.parse(localStorage.getItem("loginAttempts"));
          if (stored) currentAttempts = stored.count || 0;
        } catch {
          currentAttempts = 0;
        }
        currentAttempts += 1;

        let lockoutDuration = 0;
        let errorMessage = "Invalid email or password";

        if (currentAttempts >= 7) {
          lockoutDuration = 300 * 1000;
          errorMessage = "Account locked due to multiple failed attempts.";
        } else if (currentAttempts >= 5) {
          lockoutDuration = 120 * 1000;
          errorMessage = "Too many failed attempts. Account temporarily locked.";
        } else if (currentAttempts >= 3) {
          lockoutDuration = 30 * 1000;
          errorMessage = "Too many failed attempts.";
        } else {
          errorMessage = "Invalid email or password";
        }

        const newState = { count: currentAttempts, lockoutEnd: lockoutDuration > 0 ? Date.now() + lockoutDuration : 0 };
        localStorage.setItem("loginAttempts", JSON.stringify(newState));
        setLoginAttempts(newState);
        setError(errorMessage);
        setPassword("");
        setIsLoading(false);
        return;
      }

      localStorage.removeItem("loginAttempts");
      setLoginAttempts({ count: 0, lockoutEnd: 0 });

      const { data: { user } } = await supabase.auth.getUser();
      onLogin(user);
      navigate('/', { replace: true });
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const isLockedOut = loginAttempts.lockoutEnd > Date.now();
  const isWarning = loginAttempts.count >= 3;

  return (
    <div className="login-root">
      <div className="login-card-wrapper">
        {/* Logo */}
        <div className="login-brand">
          <div className="login-logo-wordmark">BUNKER</div>
          <p className="login-subtitle">Iniciar sesión</p>
        </div>

        {/* Form Card */}
        <div className="login-card">
          <form onSubmit={handleLogin} noValidate>
            <div className="login-field">
              <label htmlFor="email" className="login-label">Email address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="login-input"
                placeholder="you@company.com"
              />
            </div>

            <div className="login-field">
              <label htmlFor="password" className="login-label">Password</label>
              <div className="login-input-wrap">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    passwordRef.current = e.target.value;
                  }}
                  required
                  autoComplete="current-password"
                  className="login-input login-input--password"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="login-eye-btn"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="login-eye-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="login-eye-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className={`login-error ${isWarning ? "login-error--warning" : ""}`} role="alert">
                <svg className="login-error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || isLockedOut}
              className={`login-btn ${isLockedOut ? "login-btn--locked" : ""}`}
            >
              {isLoading ? (
                <>
                  <span className="login-spinner" aria-hidden="true" />
                  Signing in...
                </>
              ) : isLockedOut ? (
                <>
                  <span className="login-spinner" aria-hidden="true" />
                  Locked - {lockoutTimer}s
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>

        <p className="login-footer">© 2025 Bunker Indoor Golf. Todos los derechos reservados.</p>
      </div>
    </div>
  );
};

export default Login;
